import { Box, Breadcrumbs, Button, GlobalStyles, Typography } from '@mui/material';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Stepper from '@mui/material/Stepper';
import StepDataMapping from 'components/@home/@documents/SentDocuments/edit-steps/StepDataMapping';
import {
  buildSendReportToPatch,
  buildSubmitPayload,
  formatRequiresDigitalSignature,
  validateStep,
} from 'components/@home/@documents/SentDocuments/edit-steps/editTemplateFlow';
import SetupDocumentMeta from 'components/@home/@documents/SentDocuments/edit-steps/SetupDocumentMeta';
import StepSetupDocument from 'components/@home/@documents/SentDocuments/edit-steps/StepSetupDocument';
import { SendDrawerContext } from 'components/@home/@documents/SentDocuments/SendDrawerContext';
import extractionFileMatchesForSend from 'components/@home/@documents/SentDocuments/extractionFileMatch';
import { mapExtractedFilesToSendRows } from 'components/@home/@documents/SentDocuments/mapExtractedFilesToSendRows';
import {
  getSendDrawerStepKeys,
  sendDrawerNeedsExtractedRecipients,
} from 'components/@home/@documents/SentDocuments/sendDrawerStepKeys';
import StepDocumentAndMode from 'components/@home/@documents/SentDocuments/steps/StepDocumentAndMode/StepDocumentAndMode';
import StepFiles from 'components/@home/@documents/SentDocuments/steps/StepFiles';
import StepMessage from 'components/@home/@documents/SentDocuments/steps/StepMessage';
import StepRecipientsPicker from 'components/@home/@documents/SentDocuments/steps/StepRecipientsPicker/StepRecipientsPicker';
import StepSend from 'components/@home/@documents/SentDocuments/steps/StepSend';
import useTemplateEditor from 'components/@home/@documents/SentDocuments/useTemplateEditor';
import { getFileName } from 'components/@home/@documents/SentDocuments/steps/StepFiles/pdf-utils';
import throttle from 'lodash/throttle';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';
import { resolveDigitalSignatureMessageText } from 'utils/getDigitalSignatureCardCopy';
import Attachments from 'services/api/Attachments';
import bulkMessagings from 'store/app/entities/bulkMessaging/action';
import {
  useCreateDocumentFormatMutation,
  useDocumentFormatInlineSendMutation,
  useDocumentFormatSendMutation,
} from 'store/app/entities/SharedFilesSlice';
import authUser from 'store/selectors/authUser';
import authCompanyId from 'store/selectors/authCompanyId';
import useActions from 'utils/useActions';
import { HR_BOT_APP_ID } from 'services/constants';

/**
 * API / extraction types are `single-pdf` | `multi-pdf` | `xml`.
 * Editor state uses shorthand `pdf` plus `split`; persisting must not replace the API type.
 */
function persistedDocumentFormatType(formatFile, editorTemplate) {
  const t = formatFile?.type;
  if (t === 'xml') return 'xml';
  if (t === 'single-pdf' || t === 'multi-pdf') return t;
  return editorTemplate.split ? 'single-pdf' : 'multi-pdf';
}

const textLayerGlobalStyles = {
  '.textLayerDiv': {
    position: 'relative',
    display: 'block !important',
    '& span': {
      position: 'absolute',
      whiteSpace: 'pre',
      transformOrigin: '0% 0%',
    },
  },
};

const SendDrawerContent = () => {
  const [documentFormatSend] = useDocumentFormatSendMutation();
  const [documentFormatInlineSend] = useDocumentFormatInlineSendMutation();
  const [createDocumentFormat] = useCreateDocumentFormatMutation();
  const clearBulkMessagings = useActions(bulkMessagings.clear);
  const addBulkMessaging = useActions(bulkMessagings.add);
  const user = useSelector(authUser);
  const authEmployee = useSelector(state => state.auth?.employee);
  const companyId = useSelector(authCompanyId);
  const app = useSelector(state => state.apps.apps?.[HR_BOT_APP_ID]);
  const resets = useRef(0);
  const selectedEmployeesMapRef = useRef(new Map());
  const [editorSession, setEditorSession] = useState(0);

  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const [parsingPct, setParsingPct] = useState(0);
  const [userItems, setUserItems] = useState({});
  const [error, setError] = useState(null);
  const [nextEnabled, setNextEnabled] = useState(false);
  const [uploadedPct, setUploadedPct] = useState(0);
  const [files, setFiles] = useState(null);
  const [message, setMessage] = useState('');
  const [messageSubject, setMessageSubject] = useState('');
  const [signatureCardTitle, setSignatureCardTitle] = useState('');
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [attachmentOverride, setAttachmentOverride] = useState(null);
  const [deliveryOptions, setDeliveryOptions] = useState(() => ({
    expiresPreset: 'never',
    expiresAt: '',
    reminderIntervalDays: '',
  }));
  /** oneForAll only: parallel vs sequential signing order (send drawer). */
  const [sequential, setSequential] = useState(false);
  const [recipientTab, setRecipientTab] = useState('ommnio');
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [saveAsTemplateName, setSaveAsTemplateName] = useState('');
  /** Send step: full summary first, then progress after confirming Send. */
  const [sendPhase, setSendPhase] = useState('summary');

  const handleChangeSelectedEmployeeObjects = useCallback((ids, visibleRows) => {
    const map = selectedEmployeesMapRef.current;
    const idsNorm = (ids || []).map(id => String(id));
    const visibleIds = new Set((visibleRows || []).map(e => String(e._id)));
    (visibleRows || []).forEach(e => map.set(String(e._id), e));
    for (const mapId of [...map.keys()]) {
      if (visibleIds.has(mapId) && !idsNorm.includes(mapId)) {
        map.delete(mapId);
      }
    }
    setSelectedEmployeeIds(idsNorm);
  }, []);

  const handleRemoveRecipientChip = useCallback(employeeId => {
    const sid = String(employeeId);
    selectedEmployeesMapRef.current.delete(sid);
    setSelectedEmployeeIds(prev => prev.filter(id => String(id) !== sid));
  }, []);

  const selectedRecipientsForChips = useMemo(() => {
    const map = selectedEmployeesMapRef.current;
    return selectedEmployeeIds.map(id => {
      const sid = String(id);
      const e = map.get(sid);
      const label = e
        ? [e.firstName, e.surName].filter(Boolean).join(' ').trim() || e.name || sid
        : sid;
      return { id: sid, label };
    });
  }, [selectedEmployeeIds]);

  const realMembers = useMemo(() => {
    const members = [];
    for (const [, e] of selectedEmployeesMapRef.current) {
      const firstName =
        e.firstName || [e.firstName, e.surName].filter(Boolean).join(' ') || e.name || '';
      members.push({
        firstName,
        surName: e.surName || '',
        avatar: e.avatar,
        user: { _id: String(e._id) },
      });
    }
    emailRecipients
      .filter(r => r.email?.trim())
      .forEach((r, i) => {
        members.push({ firstName: r.name || r.email, surName: '', user: { _id: `email-${i}` } });
      });
    return members.length > 0 ? members : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- map is a ref; selectedEmployeeIds triggers recompute
  }, [selectedEmployeeIds, emailRecipients]);

  const {
    isOpen,
    file: format,
    setFile,
    close,
    allowFormatSelection,
  } = useContext(SendDrawerContext);

  const editorResetKey = useMemo(
    () =>
      `${editorSession}_${
        format?._id || format?.templateAttachment || format?.layoutSampleAttachment || ''
      }`,
    [editorSession, format?._id, format?.templateAttachment, format?.layoutSampleAttachment],
  );

  const {
    template,
    setTemplate,
    fields,
    syncFieldsToTemplate,
    canvasPdfSrc,
    uploadBusy,
    onPdfDrop,
    onReplacePdf,
    currentSignerIndex,
    setCurrentSignerIndex,
    placeholderMembers,
    sessionPdfFilesRef,
  } = useTemplateEditor({
    format,
    defaultMessage: I18n.t('HrBot.defaultMessage'),
    resetKey: editorResetKey,
    realMembers,
    /** Per-send (non-fixed): do not preload layout/template PDF — user uploads this batch's file on Document setup. */
    skipAttachmentAutoLoad: format?.documentSource !== 'fixed',
  });

  const stepKeys = useMemo(
    () => getSendDrawerStepKeys(format, { allowFormatSelection }),
    [allowFormatSelection, format],
  );

  /** Replacing the layout PDF invalidates extracted rows from a previous upload (same wizard session). */
  const handleReplacePdf = useCallback(() => {
    resets.current += 1;
    setFiles(null);
    setParsingPct(0);
    setNextEnabled(false);
    setError(null);
    onReplacePdf();
    const filesStepIdx = stepKeys.findIndex(k => k === 'ExtractedRecipients');
    if (filesStepIdx >= 0) {
      setCompleted(prev => {
        const next = { ...prev };
        for (let i = filesStepIdx; i < stepKeys.length; i += 1) {
          delete next[i];
        }
        return next;
      });
    }
  }, [onReplacePdf, stepKeys]);

  const steps = useMemo(
    () =>
      stepKeys.map(k => {
        if (
          k === 'DocumentMode' &&
          !allowFormatSelection &&
          format?.documentSource === 'per-send' &&
          !sendDrawerNeedsExtractedRecipients(format)
        ) {
          return I18n.t('HrBot.Steps.PerSendSessionDocument');
        }
        return I18n.t(`HrBot.Steps.${k}`);
      }),
    [stepKeys, allowFormatSelection, format],
  );

  const prevIsOpenRef = useRef(false);

  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    if (!wasOpen || isOpen) return;

    clearBulkMessagings();
    setUploadedPct(0);
    setFiles(null);
    setError(null);
    setNextEnabled(false);
    setMessage('');
    setMessageSubject('');
    setSignatureCardTitle('');
    setActiveStep(0);
    setCompleted({});
    setParsingPct(0);
    setUserItems({});
    setEmailRecipients([]);
    setAttachmentOverride(null);
    setDeliveryOptions({
      expiresPreset: 'never',
      expiresAt: '',
      reminderIntervalDays: '',
    });
    setSequential(false);
    setRecipientTab('ommnio');
    setSelectedEmployeeIds([]);
    selectedEmployeesMapRef.current.clear();
    setSaveAsTemplate(false);
    setSaveAsTemplateName('');
    setSendPhase('summary');
    setEditorSession(s => s + 1);
    setFile(null);
  }, [isOpen, clearBulkMessagings, setFile]);

  useEffect(() => {
    if (format?.items) {
      format.items
        .filter(i => i.selector === 'user' && i.defaultValue)
        .forEach(item => {
          let date = moment();
          let value;
          if (item.defaultValue === 'previousMonth') {
            date = date.subtract(1, 'months');
          }
          if (item.name === 'month') {
            value = date.format('MM');
          } else if (item.name === 'year') {
            value = date.format('YYYY');
          } else if (item.name === 'day') {
            value = date.format('D');
          }
          setUserItems(ui => ({
            ...ui,
            [item.name]: value,
          }));
        });
    }
  }, [format]);

  const totalSteps = () => steps.length;
  const completedSteps = () => Object.keys(completed).length;
  const isLastStep = () => activeStep === totalSteps() - 1;
  const allStepsCompleted = () => completedSteps() === totalSteps();
  const currentStepKey = stepKeys[activeStep];
  const sendBy = format?.sendBy || 'code';
  const documentSource = format?.documentSource || 'per-send';

  const mergedFormat = useMemo(
    () =>
      format
        ? {
            ...format,
            ...template,
            fields,
            type: persistedDocumentFormatType(format, template),
          }
        : null,
    [format, template, fields],
  );

  /** Primitive flag — do not put `mergedFormat` in message/title effect deps (OMM-683). */
  const requiresDigitalSignature = formatRequiresDigitalSignature(mergedFormat);

  const signingOrderRecipientCount = useMemo(() => {
    const ext = (emailRecipients || []).filter(r => r.email?.trim()).length;
    return selectedEmployeeIds.length + ext;
  }, [selectedEmployeeIds, emailRecipients]);

  const hasExtractionFiles = useCallback(() => {
    if (!files?.length) return false;
    return files.some(f => !f.error && extractionFileMatchesForSend(f, sendBy));
  }, [files, sendBy]);

  const hasExternalPayload = useCallback(() => {
    const signers = (emailRecipients || []).filter(r => r.email && String(r.email).trim());
    return signers.length > 0;
  }, [emailRecipients]);

  const resolveChannel = useCallback(() => {
    if (hasExternalPayload()) {
      return 'external';
    }
    return 'internal';
  }, [hasExternalPayload]);

  const defaultExternalMessageSubject = useMemo(() => {
    const requesterName =
      [authEmployee?.firstName, authEmployee?.surName]
        .filter(v => v != null && String(v).trim())
        .join(' ')
        .trim() || I18n.t('HrBot.SendStep.defaultSubjectRequesterFallback');
    const documentName = (format?.name || '').trim() || '—';
    return I18n.t('HrBot.SendStep.defaultMessageSubject', { requesterName, documentName });
  }, [authEmployee?.firstName, authEmployee?.surName, format?.name]);

  useEffect(() => {
    const channel = resolveChannel();
    if (channel === 'external') {
      setMessage(format?.defaultMessage ?? '');
      return;
    }
    if (requiresDigitalSignature) {
      // Ommnio (internal) chat: do not pre-fill the template body — sender must write the DM text.
      setMessage('');
    } else {
      setMessage(format?.defaultMessage || I18n.t('HrBot.defaultMessage'));
    }
  }, [format?._id, format?.defaultMessage, format?.name, resolveChannel, requiresDigitalSignature]);

  useEffect(() => {
    if (resolveChannel() !== 'external') return;
    setMessageSubject(defaultExternalMessageSubject);
  }, [format?._id, resolveChannel, defaultExternalMessageSubject]);

  useEffect(() => {
    const channel = resolveChannel();
    if (channel === 'external' || !requiresDigitalSignature) {
      setSignatureCardTitle('');
      return;
    }
    if (hasExtractionFiles()) {
      setSignatureCardTitle('');
      return;
    }
    const name = (mergedFormat?.name || format?.name || '').trim();
    setSignatureCardTitle(name ? `${name}.pdf` : '');
    // Intentionally omit full `mergedFormat` / `template` — sendReportTo edits must not wipe the title (OMM-683).
  }, [
    format?._id,
    format?.name,
    mergedFormat?.name,
    resolveChannel,
    requiresDigitalSignature,
    hasExtractionFiles,
  ]);

  const persistEditorToFormat = useCallback(() => {
    if (!format) return;
    flushSync(() => {
      setFile(f => ({
        ...f,
        ...template,
        fields,
        type: persistedDocumentFormatType(f, template),
      }));
    });
  }, [format, setFile, template, fields]);

  const abortSendPhase = useCallback(() => {
    setSendPhase('summary');
  }, []);

  const handleSend = useCallback(async () => {
    const fmt = {
      ...(format || {}),
      ...template,
      fields,
      type: format?.type,
      sendSessionAttachmentId: format?.sendSessionAttachmentId || template?.sendSessionAttachmentId,
    };
    const scheduleSigningDelivery = formatRequiresDigitalSignature(fmt);
    if (!fmt || (!fmt._id && !fmt.templateAttachment && !fmt.layoutSampleAttachment)) {
      abortSendPhase();
      return false;
    }
    setNextEnabled(false);
    setUploadedPct(0);
    let clientUploadReportsProgress = false;

    const hasInternalRecipients = hasExtractionFiles() || selectedEmployeeIds.length > 0;
    const hasEmailRecipients = hasExternalPayload();
    if (hasInternalRecipients && hasEmailRecipients) {
      setError(I18n.t('HrBot.RecipientsPicker.mixedChannelBlocked'));
      setNextEnabled(true);
      abortSendPhase();
      return false;
    }

    const channel = resolveChannel();
    const body = {
      text: message,
      isMandatory: false,
      channel,
      files: [],
      recipients: [],
    };
    if (channel === 'external') {
      const subjectTrim = (messageSubject || '').trim();
      const templateNameTrim = (fmt?.name || '').trim();
      if (subjectTrim || templateNameTrim) {
        body.messageTitle = subjectTrim || templateNameTrim;
      }
    } else if (scheduleSigningDelivery) {
      const titleTrim = (signatureCardTitle || '').trim();
      if (titleTrim && !hasExtractionFiles()) {
        body.messageTitle = titleTrim;
      }
    }
    if (scheduleSigningDelivery && channel === 'internal') {
      body.text = resolveDigitalSignatureMessageText(body.messageTitle, body.text);
    }
    if (scheduleSigningDelivery) {
      if (deliveryOptions.expiresPreset === 'never') {
        body.expiresAt = null;
      } else if (deliveryOptions.expiresAt && String(deliveryOptions.expiresAt).trim()) {
        body.expiresAt = deliveryOptions.expiresAt;
      }
      if (deliveryOptions.reminderIntervalDays) {
        body.reminderIntervalDays = Number(deliveryOptions.reminderIntervalDays);
      }
    }
    const signingModeForSend = template.signingMode || format?.signingMode || 'onePerMember';
    body.sequential = !!(signingModeForSend === 'oneForAll' && sequential === true);

    if (channel === 'internal') {
      if (selectedEmployeeIds.length > 0 && !hasExtractionFiles()) {
        body.internalRecipients = selectedEmployeeIds;
      }
    }

    let attachmentId =
      attachmentOverride?._id || attachmentOverride || fmt.sendSessionAttachmentId || null;

    if (channel === 'internal') {
      if (hasExtractionFiles()) {
        clientUploadReportsProgress = true;
        const filesToSend = files.filter(
          f =>
            !f.error &&
            (sendBy === 'id' ? f.employee?.id : f.employee?.user?._id || f.employee?.user),
        );
        const filesMap = filesToSend.reduce(
          (acc, file) => acc.set(file.file, [...(acc.get(file.file) || []), file]),
          new Map(),
        );
        const filesToUpload = Array.from(filesMap.entries());
        const uploaded = filesToUpload.map(() => 0);
        const updatePct = throttle(() => {
          const pct = Math.round(uploaded.reduce((acc, curr) => acc + curr, 0) / uploaded.length);
          setUploadedPct(pct);
          if (pct === 100) {
            setNextEnabled(true);
          }
        }, 200);
        const attachmentsArr = await Promise.all(
          filesToUpload.map(async ([file, origFiles], idx) => {
            const fileName = origFiles?.length === 1 ? origFiles[0].newFileName : null;
            const { _id: attachment } = await Attachments.upload(
              companyId,
              'none',
              file,
              null,
              progressEvent => {
                uploaded[idx] = (progressEvent.loaded * 100) / progressEvent.total;
                updatePct();
              },
              null,
              fileName,
            );
            return filesMap.get(file).map(f => ({
              ...f,
              attachment,
            }));
          }),
        );
        body.files = mapExtractedFilesToSendRows(
          attachmentsArr.flat(),
          fmt,
          userItems,
          getFileName,
          {
            includePassword: a => !!(a.dni && fmt.encryptPdfWithDni !== false),
          },
        );
      } else if (!hasExtractionFiles()) {
        if (documentSource === 'fixed') {
          body.attachment =
            attachmentId || fmt.templateAttachment?._id || fmt.templateAttachment || null;
        } else if (documentSource === 'per-send') {
          const sid = attachmentId || fmt.sendSessionAttachmentId || null;
          if (!sid) {
            setError(I18n.t('HrBot.SendStep.internalPerSendMissingFile'));
            setNextEnabled(true);
            abortSendPhase();
            return false;
          }
          body.attachment = sid;
        }
      }
    }

    if (channel === 'external') {
      if (hasExternalPayload()) {
        body.recipients = emailRecipients
          .filter(r => r.email?.trim())
          .map(r => ({
            email: String(r.email).trim(),
            ...(r.phone?.trim() ? { phone: String(r.phone).trim() } : {}),
            name: r.name || '',
            ...(r.department?.trim() ? { department: String(r.department).trim() } : {}),
            ...(r.jobTitle?.trim() ? { jobTitle: String(r.jobTitle).trim() } : {}),
            ...(r.code?.trim() ? { code: String(r.code).trim() } : {}),
            action: 'signer',
            order: r.order,
          }));
        if (documentSource === 'fixed') {
          body.attachment =
            attachmentId || fmt.templateAttachment?._id || fmt.templateAttachment || null;
        } else if (documentSource === 'per-send') {
          const sid = body.attachment || attachmentId || fmt.sendSessionAttachmentId;
          if (sid) {
            body.attachment = sid;
          } else {
            const first = files?.find(f => !f.error && f.file);
            if (!first?.file) {
              setError(I18n.t('HrBot.SendStep.externalPerSendMissingFile'));
              setNextEnabled(true);
              abortSendPhase();
              return false;
            }
            clientUploadReportsProgress = true;
            const updateExternalUploadPct = throttle(progressEvent => {
              const total = progressEvent.total;
              if (!total) return;
              setUploadedPct(Math.round((progressEvent.loaded * 100) / total));
            }, 200);
            const { _id } = await Attachments.upload(
              companyId,
              'none',
              first.file,
              null,
              progressEvent => {
                updateExternalUploadPct(progressEvent);
              },
              null,
              null,
            );
            setUploadedPct(100);
            attachmentId = _id;
            body.attachment = attachmentId;
          }
        }
      }
    }

    const isEphemeral = !fmt._id;
    if (isEphemeral && saveAsTemplate) {
      if (!saveAsTemplateName?.trim()) {
        setError(I18n.t('HrBot.SendStep.saveAsTemplateNameRequired'));
        setNextEnabled(true);
        abortSendPhase();
        return false;
      }
    }

    if (!clientUploadReportsProgress) {
      setUploadedPct(100);
    }

    body.fields = fields;
    Object.assign(body, buildSendReportToPatch(template.sendReportTo));

    try {
      let bulkMessaging;
      if (isEphemeral && !saveAsTemplate) {
        const tpl = buildSubmitPayload({ ...template, fields, name: fmt.name || 'send' });
        const res = await documentFormatInlineSend({
          template: tpl,
          send: body,
        }).unwrap();
        bulkMessaging = res?.data ?? res;
      } else {
        let formatId = fmt._id;
        if (isEphemeral && saveAsTemplate) {
          const tpl = buildSubmitPayload({
            ...template,
            fields,
            name: saveAsTemplateName.trim(),
          });
          const created = await createDocumentFormat(tpl).unwrap();
          const createdDoc = created?.data?.documentFormat || created?.documentFormat;
          formatId = createdDoc?._id;
        }
        if (!formatId) {
          setError(I18n.t('HrBot.Error'));
          setNextEnabled(true);
          abortSendPhase();
          return false;
        }
        const res = await documentFormatSend({
          id: formatId,
          body,
        }).unwrap();
        bulkMessaging = res?.data ?? res;
      }
      if (bulkMessaging && bulkMessaging._id) {
        addBulkMessaging(bulkMessaging);
      }
      setNextEnabled(true);
      return true;
    } catch (e) {
      setError(e?.data?.error?.message || e?.message || String(e));
      setNextEnabled(true);
      abortSendPhase();
      return false;
    }
  }, [
    format,
    template,
    fields,
    documentFormatSend,
    documentFormatInlineSend,
    createDocumentFormat,
    files,
    sendBy,
    documentSource,
    message,
    messageSubject,
    companyId,
    userItems,
    emailRecipients,
    attachmentOverride,
    deliveryOptions,
    selectedEmployeeIds,
    resolveChannel,
    hasExternalPayload,
    hasExtractionFiles,
    addBulkMessaging,
    saveAsTemplate,
    saveAsTemplateName,
    signatureCardTitle,
    abortSendPhase,
    sequential,
  ]);

  const handleNext = async () => {
    if (isLastStep()) {
      await handleSend();
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStepKey === 'Send') {
      setSendPhase('summary');
    }
    setActiveStep(prev => prev - 1);
  };

  const handleStep = step => () => {
    setActiveStep(step);
  };

  /** Map a Step Send "Edit" affordance to the right wizard step. */
  const handleEditStep = useCallback(
    target => {
      const aliasToCandidates = {
        document: ['DocumentMode', 'SetupDocument', 'DataMapping'],
        recipients: ['Recipients', 'RecipientsPicker', 'ExtractedRecipients'],
        delivery: ['Message'],
      };
      const candidates = aliasToCandidates[target] || [];
      const idx = stepKeys.findIndex(k => candidates.includes(k));
      if (idx >= 0) {
        setSendPhase('summary');
        setActiveStep(idx);
      }
    },
    [stepKeys],
  );

  const handleComplete = async () => {
    if (currentStepKey === 'SetupDocument' || currentStepKey === 'DataMapping') {
      persistEditorToFormat();
      if (currentStepKey === 'SetupDocument') {
        const sessionPdfs = sessionPdfFilesRef.current;
        if (sessionPdfs?.length && sendDrawerNeedsExtractedRecipients(format)) {
          setFiles(prev => (prev?.length ? prev : sessionPdfs.map(f => ({ file: f }))));
        }
      }
    }
    if (currentStepKey === 'Send') {
      if (sendPhase === 'summary') {
        setSendPhase('sending');
        const ok = await handleSend();
        if (ok) {
          setCompleted(prev => ({ ...prev, [activeStep]: true }));
        }
        return;
      }
      return;
    }
    setCompleted({ ...completed, [activeStep]: true });
    await handleNext();
  };

  const handleReset = () => {
    setFiles(null);
    setParsingPct(0);
    setUserItems({});
    setMessage('');
    setMessageSubject('');
    setSignatureCardTitle('');
    setError(null);
    setEmailRecipients([]);
    setAttachmentOverride(null);
    setDeliveryOptions({
      expiresPreset: 'never',
      expiresAt: '',
      reminderIntervalDays: '',
    });
    setSequential(false);
    setRecipientTab('ommnio');
    setSelectedEmployeeIds([]);
    selectedEmployeesMapRef.current.clear();
    setSaveAsTemplate(false);
    setSaveAsTemplateName('');
    setSendPhase('summary');
    setActiveStep(0);
    setCompleted({});
    setUploadedPct(0);
    setNextEnabled(false);
    resets.current += 1;
    /** Re-run `useTemplateEditor` init (clears canvas / session PDF) even when the same format stays selected. */
    setEditorSession(s => s + 1);
    /** “Send from tracking” / toolbar: clear chosen template so Document & mode starts empty. */
    if (allowFormatSelection) {
      setFile(null);
    }
  };

  const firstStepKey = stepKeys[0];
  const disableResetOnFirstStep =
    firstStepKey === 'ExtractedRecipients'
      ? !files
      : firstStepKey === 'DocumentMode'
        ? !format
        : firstStepKey === 'Recipients' || firstStepKey === 'RecipientsPicker'
          ? !emailRecipients?.length && !selectedEmployeeIds?.length
          : false;

  const canUseXml = !!app?.settings?.hrbotAllowXmlTemplates;
  /** Saved template may still carry attachment ids while `skipAttachmentAutoLoad` keeps canvas empty — require an actual PDF on canvas before continuing. */
  const setupStepValid =
    currentStepKey === 'SetupDocument'
      ? validateStep('setupDocument', { ...template, fields }) && !!canvasPdfSrc
      : currentStepKey === 'DataMapping'
        ? validateStep('dataMapping', { ...template, fields })
        : true;

  const getStepContent = key => {
    switch (key) {
      case 'DocumentMode':
        return <StepDocumentAndMode setNextEnabled={setNextEnabled} setError={setError} />;
      case 'SetupDocument':
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              flex: 1,
              minHeight: 0,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
              <StepSetupDocument
                template={template}
                setTemplate={setTemplate}
                fields={fields}
                syncFieldsToTemplate={syncFieldsToTemplate}
                canvasPdfSrc={canvasPdfSrc}
                currentSignerIndex={currentSignerIndex}
                setCurrentSignerIndex={setCurrentSignerIndex}
                placeholderMembers={placeholderMembers}
                uploadBusy={uploadBusy}
                onPdfDrop={onPdfDrop}
                onReplacePdf={handleReplacePdf}
                canUseXml={canUseXml}
                sendSessionPdfGate={format?.documentSource !== 'fixed'}
              />
            </Box>
            <Box
              sx={{
                width: { xs: '100%', md: 300 },
                flexShrink: 0,
                borderLeft: { md: 1 },
                borderTop: { xs: 1, md: 0 },
                borderColor: { md: 'divider', xs: 'divider' }, // If I remove breakpoint, the border is black
                pl: { md: 2 },
                pt: { xs: 2, md: 0 },
              }}
            >
              <SetupDocumentMeta
                template={template}
                setTemplate={setTemplate}
                fields={fields}
                showUploadNewEachSend={false}
                canvasPdfSrc={canvasPdfSrc}
                uploadBusy={uploadBusy}
                onReplacePdf={handleReplacePdf}
                sendSessionPdfGate={format?.documentSource !== 'fixed'}
              />
            </Box>
          </Box>
        );
      case 'DataMapping':
        return <StepDataMapping template={template} setTemplate={setTemplate} />;
      case 'ExtractedRecipients':
        return (
          <StepFiles
            resets={resets}
            parsingPct={parsingPct}
            setParsingPct={setParsingPct}
            setError={setError}
            error={error}
            nextEnabled={nextEnabled}
            setNextEnabled={setNextEnabled}
            userItems={userItems}
            files={files}
            setFiles={setFiles}
            user={user}
            format={mergedFormat}
            hideFindColumn
          />
        );
      case 'Recipients':
      case 'RecipientsPicker':
        return (
          <StepRecipientsPicker
            format={format}
            recipientTab={recipientTab}
            onRecipientTab={setRecipientTab}
            selectedEmployeeIds={selectedEmployeeIds}
            onChangeSelectedEmployeeIds={setSelectedEmployeeIds}
            onChangeSelectedEmployeeObjects={handleChangeSelectedEmployeeObjects}
            selectedRecipientsForChips={selectedRecipientsForChips}
            onRemoveRecipientChip={handleRemoveRecipientChip}
            emailRecipients={emailRecipients}
            setEmailRecipients={setEmailRecipients}
            setNextEnabled={setNextEnabled}
            setError={setError}
          />
        );
      case 'Message':
        return (
          <StepMessage
            setError={setError}
            setNextEnabled={setNextEnabled}
            userItems={userItems}
            setUserItems={setUserItems}
            files={files}
            setMessage={setMessage}
            message={message}
            messageSubject={messageSubject}
            setMessageSubject={setMessageSubject}
            signatureCardTitle={signatureCardTitle}
            setSignatureCardTitle={setSignatureCardTitle}
            isRoutedSend={hasExtractionFiles()}
            deliveryOptions={deliveryOptions}
            setDeliveryOptions={setDeliveryOptions}
            signingMode={mergedFormat?.signingMode || 'onePerMember'}
            signingOrderRecipientCount={signingOrderRecipientCount}
            sequential={sequential}
            setSequential={setSequential}
            showDeliveryExpiryReminders={requiresDigitalSignature}
            sendChannel={resolveChannel()}
            emailRecipients={emailRecipients}
            sendReportTo={template.sendReportTo}
            onSendReportToChange={v => setTemplate(t => ({ ...t, sendReportTo: v }))}
          />
        );
      case 'Send':
        return (
          <StepSend
            messageSubject={messageSubject}
            message={message}
            signatureCardTitle={signatureCardTitle}
            isRoutedSend={hasExtractionFiles()}
            uploadedPct={uploadedPct}
            setError={setError}
            setNextEnabled={setNextEnabled}
            userItems={userItems}
            files={files}
            format={mergedFormat}
            emailRecipients={emailRecipients}
            sendChannel={resolveChannel()}
            internalPickerCount={
              !hasExtractionFiles() && selectedEmployeeIds.length > 0
                ? selectedEmployeeIds.length
                : 0
            }
            saveAsTemplate={saveAsTemplate}
            setSaveAsTemplate={setSaveAsTemplate}
            saveAsTemplateName={saveAsTemplateName}
            setSaveAsTemplateName={setSaveAsTemplateName}
            sendPhase={sendPhase}
            deliveryOptions={deliveryOptions}
            sequential={sequential}
            signingOrderRecipientCount={signingOrderRecipientCount}
            onEditStep={handleEditStep}
          />
        );
      default:
        return null;
    }
  };

  const setupOrMappingStep = currentStepKey === 'SetupDocument' || currentStepKey === 'DataMapping';
  /** Setup / data-mapping mirror EditDrawer (`validateStep` only); child steps do not drive `nextEnabled`. */
  const nextDisabled = !!error || (setupOrMappingStep ? !setupStepValid : !nextEnabled);

  const sendInProgress =
    currentStepKey === 'Send' && sendPhase === 'sending' && !allStepsCompleted();

  if (!isOpen || (!format && !allowFormatSelection)) {
    return null;
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '100%',
        overflow: 'hidden',
      }}
    >
      <GlobalStyles styles={textLayerGlobalStyles} />
      <Breadcrumbs sx={{ mb: 3 }}>
        <Typography variant="h6" component="h1">
          {I18n.t('HrBot.tabs.send.title', {
            name: format?.name || I18n.t('HrBot.DocumentStep.document'),
          })}
        </Typography>
      </Breadcrumbs>

      <Stepper activeStep={activeStep}>
        {steps.map((label, index) => (
          <Step key={label} completed={completed[index]}>
            <StepButton onClick={handleStep(index)}>{label}</StepButton>
          </Step>
        ))}
      </Stepper>

      <Box
        sx={{
          mt: 2,
          mb: 1,
          flexGrow: 1,
          flexShrink: 1,
          minHeight: 0,
          display: 'flex',
          maxHeight: '100%',
          overflow: 'auto',
          flexDirection: 'column',
        }}
      >
        {getStepContent(currentStepKey)}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'baseline',
          justifyContent: 'flex-end',
        }}
      >
        {(!parsingPct || parsingPct === 100) && error && (
          <Typography color="error" variant="caption" sx={{ mr: 'auto' }}>
            {error}
          </Typography>
        )}
        {!!parsingPct && parsingPct < 100 && (
          <Typography variant="caption" sx={{ mr: 'auto' }}>
            {I18n.t('HrBot.parsing', { pct: parsingPct })}
          </Typography>
        )}
        <Button
          disabled={(activeStep === 0 && disableResetOnFirstStep) || sendInProgress}
          onClick={allStepsCompleted() || activeStep === 0 ? handleReset : handleBack}
          sx={{ mr: 1 }}
        >
          {I18n.t(allStepsCompleted() || activeStep === 0 ? 'HrBot.Reset' : 'HrBot.Back')}
        </Button>
        {!allStepsCompleted() && (
          <Button
            disabled={nextDisabled}
            variant="contained"
            color="primary"
            onClick={handleComplete}
          >
            {isLastStep() ? I18n.t('HrBot.Send') : I18n.t('HrBot.Next')}
          </Button>
        )}
        {allStepsCompleted() && (
          <Button disabled={!nextEnabled} variant="contained" color="primary" onClick={close}>
            {I18n.t('HrBot.Close')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default SendDrawerContent;
